import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { MetricCard } from './AdminUi';
import { formatCurrency, formatRelativeTime } from './utils';

const EmptyTableRow = ({ colSpan, children }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center">
      <Typography variant="body2" color="text.secondary">{children}</Typography>
    </TableCell>
  </TableRow>
);

const ComplianceTab = ({
  settings,
  financeOverview,
  loadingFinance,
  exportingFinance,
  onExportFinanceReport,
}) => {
  const receiptsEnabled = financeOverview?.billing?.receiptsEnabled ?? false;
  const vatInvoicesEnabled = financeOverview?.billing?.vatInvoicesEnabled ?? false;
  const refundWindow = financeOverview?.billing?.refundWindowDays ?? 0;
  const refundPolicy = financeOverview?.billing?.refundPolicy || 'No refund policy recorded';
  const userCap = financeOverview?.userCap || {};
  const userCapLimit = userCap.limit;
  const userCapLabel = userCapLimit
    ? `${userCap.current || 0} / ${userCapLimit}`
    : `${userCap.current || 0} users`;
  const userCapDescription = userCapLimit
    ? `${userCap.remaining ?? 0} seats remaining`
    : 'Unlimited registrations';

  return (
    <Stack spacing={3}>
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Compliance</Typography>
        {settings?.compliance?.termsUrl ? (
          <Stack spacing={1}>
            <Typography variant="body1">Terms &amp; Conditions URL</Typography>
            <Typography variant="body2" color="text.secondary">{settings.compliance.termsUrl}</Typography>
            <Typography variant="body2" color="text.secondary">
              Current version: {settings.compliance.termsVersion || 'Unversioned'}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Terms &amp; Conditions have not been configured yet. Publish your legal copy and set the URL here so that onboarding flows can reference it.
          </Typography>
        )}
      </Paper>

      <Paper elevation={0} sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2}>
          <Typography variant="h6">Finance &amp; billing oversight</Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={onExportFinanceReport}
            disabled={exportingFinance || loadingFinance}
          >
            {exportingFinance ? 'Exporting...' : 'Export CSV'}
          </Button>
        </Stack>

        {loadingFinance ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !financeOverview ? (
          <Typography variant="body2" color="text.secondary">Unable to load finance overview.</Typography>
        ) : (
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Receipts enabled"
                  value={receiptsEnabled ? 'Yes' : 'No'}
                  description={`VAT invoices ${vatInvoicesEnabled ? 'enabled' : 'disabled'}`}
                  icon={<CheckCircleIcon color={receiptsEnabled ? 'success' : 'warning'} />}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Refund window"
                  value={`${refundWindow} days`}
                  description={refundPolicy}
                  icon={<WarningAmberIcon color="warning" />}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="User cap"
                  value={userCapLabel}
                  description={userCapDescription}
                  icon={<CheckCircleIcon color="primary" />}
                />
              </Grid>
            </Grid>

            <Stack spacing={2}>
              <Typography variant="subtitle1">Recent refunds</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Entity</TableCell>
                      <TableCell>Credits</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Occurred</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(financeOverview.refunds || []).slice(0, 5).map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{entry.entityName}</TableCell>
                        <TableCell>{entry.credits}</TableCell>
                        <TableCell>{formatCurrency(entry.amountCents, entry.currency)}</TableCell>
                        <TableCell>{formatRelativeTime(entry.occurredAt)}</TableCell>
                      </TableRow>
                    ))}
                    {!(financeOverview.refunds || []).length && (
                      <EmptyTableRow colSpan={4}>No refunds recorded.</EmptyTableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Stack>

            <Stack spacing={2}>
              <Typography variant="subtitle1">Recent receipts</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Entity</TableCell>
                      <TableCell>Credits</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Occurred</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(financeOverview.receipts || []).slice(0, 5).map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{entry.entityName}</TableCell>
                        <TableCell>{entry.credits}</TableCell>
                        <TableCell>{formatCurrency(entry.amountCents, entry.currency)}</TableCell>
                        <TableCell>{formatRelativeTime(entry.occurredAt)}</TableCell>
                      </TableRow>
                    ))}
                    {!(financeOverview.receipts || []).length && (
                      <EmptyTableRow colSpan={4}>No receipts recorded.</EmptyTableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Stack>

            <Stack spacing={2}>
              <Typography variant="subtitle1">VAT invoices</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>University</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Recorded</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(financeOverview.invoices || []).slice(0, 5).map((invoice) => (
                      <TableRow key={invoice.invoiceId}>
                        <TableCell>{invoice.universityName}</TableCell>
                        <TableCell>{invoice.status}</TableCell>
                        <TableCell>{formatCurrency(invoice.amountPaid || invoice.amountDue, invoice.currency)}</TableCell>
                        <TableCell>{formatRelativeTime(invoice.recordedAt || invoice.paidAt || invoice.dueDate)}</TableCell>
                      </TableRow>
                    ))}
                    {!(financeOverview.invoices || []).length && (
                      <EmptyTableRow colSpan={4}>No invoices recorded.</EmptyTableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};

export default ComplianceTab;
